import { useCallback, useRef } from 'react';

// Audio context for generating sounds
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
};

// Sound effect configurations
const SOUND_EFFECTS: Record<string, { frequencies: number[]; duration: number; type: OscillatorType }> = {
  clap: {
    frequencies: [800, 1200, 1600],
    duration: 0.08,
    type: 'square',
  },
  applause: {
    frequencies: [600, 900, 1200, 1500],
    duration: 0.15,
    type: 'triangle',
  },
  pop: {
    frequencies: [400, 600],
    duration: 0.05,
    type: 'sine',
  },
  heart: {
    frequencies: [440, 554, 659],
    duration: 0.12,
    type: 'sine',
  },
  fire: {
    frequencies: [200, 300, 150],
    duration: 0.1,
    type: 'sawtooth',
  },
  celebration: {
    frequencies: [523, 659, 784, 1047],
    duration: 0.2,
    type: 'triangle',
  },
};

// Map emoji to sound effect
const EMOJI_SOUND_MAP: Record<string, string> = {
  '👏': 'clap',
  '🙌': 'applause',
  '❤️': 'heart',
  '💕': 'heart',
  '😍': 'heart',
  '🔥': 'fire',
  '🎉': 'celebration',
  '😂': 'pop',
};

export const useReactionSounds = () => {
  const lastPlayedRef = useRef<number>(0);
  const minInterval = 100; // Minimum ms between sounds to prevent audio spam

  const playSoundEffect = useCallback((effectName: string, volume: number = 0.3) => {
    const now = Date.now();
    if (now - lastPlayedRef.current < minInterval) return;
    lastPlayedRef.current = now;

    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const effect = SOUND_EFFECTS[effectName] || SOUND_EFFECTS.pop;
      const currentTime = ctx.currentTime;

      effect.frequencies.forEach((freq, index) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = effect.type;
        oscillator.frequency.setValueAtTime(freq, currentTime);

        // Create envelope
        gainNode.gain.setValueAtTime(0, currentTime);
        gainNode.gain.linearRampToValueAtTime(volume, currentTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + effect.duration);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        const startDelay = index * 0.02;
        oscillator.start(currentTime + startDelay);
        oscillator.stop(currentTime + effect.duration + startDelay);
      });
    } catch (error) {
      console.warn('Could not play sound effect:', error);
    }
  }, []);

  const playReactionSound = useCallback(
    (emoji: string) => {
      const effectName = EMOJI_SOUND_MAP[emoji] || 'pop';
      playSoundEffect(effectName);
    },
    [playSoundEffect]
  );

  // Play applause sound (can be triggered by host or for special moments)
  const playApplause = useCallback(() => {
    // Play multiple claps in sequence for applause effect
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const clapCount = 5 + Math.floor(Math.random() * 5);
    for (let i = 0; i < clapCount; i++) {
      setTimeout(() => {
        playSoundEffect('clap', 0.2 + Math.random() * 0.15);
      }, i * 80 + Math.random() * 40);
    }
  }, [playSoundEffect]);

  return {
    playReactionSound,
    playSoundEffect,
    playApplause,
  };
};
